import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import type {TicketDetailHeader_ticket$key} from './__generated__/TicketDetailHeader_ticket.graphql';
import {TicketStatusBadge} from './TicketStatusBadge';
import {PriorityBadge} from './PriorityBadge';
import {TicketBreadcrumb} from './TicketBreadcrumb';
import {TicketActionBar} from './TicketActionBar';
import {TicketTabs} from './TicketTabs';
import {ClockIcon} from './ClockIcon';
import {useWatchTicket} from '../hooks/useWatchTicket';
import {formatDateFull} from '../utils/formatDate';
import type {TicketPriority, TicketStatus} from '../types/ticket';

const TicketHeaderFragment = graphql`
	fragment TicketDetailHeader_ticket on Ticket {
		id
		subject
		description
		status
		priority
		slug
		number
		createdAt
		assigneeId
		isWatching
		watcherCount
		totalLoggedHours
		totalBillableHours
		requester {
			name
		}
	}
`;

interface Props {
	ticket: TicketDetailHeader_ticket$key;
	activeTab?: string;
	onTabChange?: (tabId: string) => void;
	linkCount?: number;
	timeEntryCount?: number;
	onResolve?: () => void;
	saving?: boolean;
	/** ACC-441: perm-gated by the route; threads through to the action bar. */
	onDelete?: () => void;
}

const Header = styled.header`
	margin-bottom: 24px;
`;
const TopRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const BadgeRow = styled.div`
	margin-top: 16px;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
`;
const SlugChip = styled.span`
	border-radius: 4px;
	background: #f1f5f9;
	padding: 2px 8px;
	font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
	font-size: 12px;
	line-height: 16px;
	font-weight: 500;
	color: #475569;
`;
const Subject = styled.h1`
	margin: 12px 0 0;
	font: ${p => p.theme.ticketing.font.heading2};
	color: #0f172a;
`;
const MetaRow = styled.div`
	margin-top: 8px;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	font: ${p => p.theme.ticketing.font.body3};
	color: ${p => p.theme.ticketing.color.textSecondary};
`;
const Strong = styled.span`
	font-weight: 500;
	color: #334155;
`;
const LoggedHours = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
`;
const Billable = styled.span`
	color: #94a3b8;
`;
const TabsRow = styled.div`
	margin-top: 20px;
`;

function formatLoggedHours(hours: number): string {
	return Number(hours.toFixed(2)).toString();
}

function formatOpenedTime(iso: string): string {
	const date = new Date(iso);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMins / 60);
	const diffDays = Math.floor(diffHours / 24);

	if (diffDays > 0) {
		return formatDateFull(iso);
	}
	if (diffHours > 0) return `${diffHours}h ago`;
	if (diffMins > 0) return `${diffMins}m ago`;
	return 'just now';
}

export function TicketDetailHeader({
	ticket,
	activeTab = 'overview',
	onTabChange,
	linkCount,
	timeEntryCount,
	onResolve,
	saving,
	onDelete,
}: Props) {
	const data = useFragment<TicketDetailHeader_ticket$key>(TicketHeaderFragment, ticket);
	const {watch, unwatch, inFlight: watchInFlight} = useWatchTicket();

	const tabs = [
		{id: 'overview', label: 'Overview'},
		{id: 'activity', label: 'Activity'},
		{id: 'stream', label: 'Stream'},
		{id: 'attachments', label: 'Attachments'},
		{id: 'time', label: 'Time', count: timeEntryCount || null},
		{id: 'tasks', label: 'Tasks'},
		{id: 'related', label: 'Related', count: linkCount || null},
	];

	return (
		<Header>
			<TopRow>
				<TicketBreadcrumb slug={data.slug} />
				<TicketActionBar
					status={data.status}
					assigneeId={data.assigneeId ?? null}
					isWatching={data.isWatching}
					watchCount={data.watcherCount}
					onWatch={() => watch(data.id)}
					onUnwatch={() => unwatch(data.id)}
					watchInFlight={watchInFlight}
					onResolve={onResolve ?? (() => {})}
					saving={saving}
					onDelete={onDelete}
				/>
			</TopRow>

			<BadgeRow>
				<SlugChip>{data.slug}</SlugChip>
				<TicketStatusBadge status={data.status as TicketStatus} />
				<PriorityBadge priority={data.priority as TicketPriority} />
			</BadgeRow>

			<Subject>{data.subject}</Subject>

			<MetaRow>
				{data.requester?.name && (
					<>
						<span>
							Opened by <Strong>{data.requester.name}</Strong>
						</span>
						<span aria-hidden="true">·</span>
					</>
				)}
				<span>{formatOpenedTime(data.createdAt)}</span>
				{data.totalLoggedHours > 0 && (
					<>
						<span aria-hidden="true">·</span>
						<LoggedHours>
							<ClockIcon size={14} />
							{formatLoggedHours(data.totalLoggedHours)}h logged
							{data.totalBillableHours !== data.totalLoggedHours && (
								<Billable>({formatLoggedHours(data.totalBillableHours)}h billable)</Billable>
							)}
						</LoggedHours>
					</>
				)}
			</MetaRow>

			<TabsRow>
				<TicketTabs activeTab={activeTab} onTabChange={onTabChange} tabs={tabs} />
			</TabsRow>
		</Header>
	);
}
